// Guided, full-screen recorder for a result video. Rear camera, one continuous clip; "Next shot"
// drops a chapter marker and moves the instruction on. Recording in-app is what earns the
// "Verified capture" label, so it is used on phones too (unlike the evidence recorder, which
// hands mobile to the OS camera).
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getPositionPromise } from '../../../hooks/utils/geolocation';
import type { IVideoChapter } from '../../../lib/result-video-api';
import {
	deviceInfo,
	extensionFor,
	formatClock,
	formatMegabytes,
	type IShot,
	pickRecorderMime,
	SHOTS,
} from '../../../lib/result-video-shots';
import { CloseSVG } from '../../utils/svgs';

export interface IRecordedVideo {
	file: File;
	durationSec: number;
	captureStartedAt: string;
	captureEndedAt: string;
	chapters: IVideoChapter[];
	coords: { latitude: number; longitude: number; accuracy?: number | null } | null;
	deviceInfo: string;
	wifiOnly: boolean;
}

export interface IPartyScore {
	name: string;
	votes: number;
}

interface IProps {
	pollingUnitName?: string;
	pollingUnitCode: string;
	/** The figures the agent entered — a read-along on the scores shot, never burned into the video. */
	partyScores?: IPartyScore[];
	maxDurationSec?: number;
	onUse: (video: IRecordedVideo) => void;
	onClose: () => void;
}

type Phase = 'requesting' | 'brief' | 'recording' | 'review' | 'error';

const ERRORS: Record<string, string> = {
	'permission-denied':
		'Camera or microphone access is blocked. Allow both for this site in your browser settings, then try again.',
	'no-camera': 'No camera was found on this device.',
	'in-use': 'Another app is using the camera. Close it and try again.',
	'unsupported': 'This browser can’t record video here. Choose a video from your phone instead.',
	'unknown': 'The camera didn’t start. Try again, or choose a video from your phone instead.',
};

const normalizeError = (err: unknown): string => {
	if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
		if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') return 'permission-denied';
		if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError' || err.name === 'OverconstrainedError')
			return 'no-camera';
		if (err.name === 'NotReadableError' || err.name === 'TrackStartError') return 'in-use';
	}
	return 'unknown';
};

const WARN_BEFORE_END_SEC = 15;
const LOW_LIGHT_LUMA = 45;

const vibrate = (ms: number) => {
	try {
		navigator.vibrate?.(ms);
	} catch {
		// Not supported — haptics are a nicety.
	}
};

const meteredConnectionKnown = (): boolean => {
	try {
		return typeof (navigator as Navigator & { connection?: { type?: string } }).connection?.type === 'string';
	} catch {
		return false;
	}
};

const ResultVideoRecorder: FC<IProps> = ({
	pollingUnitName,
	pollingUnitCode,
	partyScores = [],
	maxDurationSec = 180,
	onUse,
	onClose,
}) => {
	const [phase, setPhase] = useState<Phase>('requesting');
	const [errorKey, setErrorKey] = useState('unknown');
	const [elapsed, setElapsed] = useState(0);
	const [shotIndex, setShotIndex] = useState(0);
	const [chapters, setChapters] = useState<IVideoChapter[]>([]);
	const [checked, setChecked] = useState<Set<number>>(new Set());
	const [lowLight, setLowLight] = useState(false);
	const [coords, setCoords] = useState<IRecordedVideo['coords']>(null);
	const [review, setReview] = useState<{
		url: string;
		file: File;
		durationSec: number;
		startedAt: string;
		endedAt: string;
	} | null>(null);
	const [wifiOnly, setWifiOnly] = useState(false);
	// Closing mid-recording discards the take, so it needs a second tap — never a native dialog.
	const [confirmDiscard, setConfirmDiscard] = useState(false);

	const liveRef = useRef<HTMLVideoElement>(null);
	const reviewRef = useRef<HTMLVideoElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const recorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const startedAtRef = useRef<number>(0);
	const tickRef = useRef<number | null>(null);
	const lumaRef = useRef<number | null>(null);
	const closeRef = useRef<HTMLButtonElement>(null);

	const shot: IShot = SHOTS[shotIndex];
	const isLastShot = shotIndex === SHOTS.length - 1;

	const stopTracks = useCallback(() => {
		streamRef.current?.getTracks().forEach((t) => t.stop());
		streamRef.current = null;
	}, []);

	const clearTimers = useCallback(() => {
		if (tickRef.current) window.clearInterval(tickRef.current);
		if (lumaRef.current) window.clearInterval(lumaRef.current);
		tickRef.current = null;
		lumaRef.current = null;
	}, []);

	const openCamera = useCallback(async () => {
		setPhase('requesting');
		if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
			setErrorKey('unsupported');
			setPhase('error');
			return;
		}
		try {
			// 720p: the scores must be legible on a reviewer's screen. Rear camera.
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
				audio: true,
			});
			streamRef.current = stream;
			if (liveRef.current) liveRef.current.srcObject = stream;
			setPhase('brief');
		} catch (err) {
			setErrorKey(normalizeError(err));
			setPhase('error');
		}
	}, []);

	useEffect(() => {
		void openCamera();
		// A fix for the provenance strip; never blocks recording.
		void getPositionPromise()
			.then((p) => setCoords({ latitude: p.latitude, longitude: p.longitude, accuracy: p.accuracy }))
			.catch(() => setCoords(null));
		closeRef.current?.focus();
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
			clearTimers();
			const recorder = recorderRef.current;
			if (recorder && recorder.state !== 'inactive') {
				recorder.onstop = null;
				try {
					recorder.stop();
				} catch {
					// Already stopping.
				}
			}
			streamRef.current?.getTracks().forEach((t) => t.stop());
		};
	}, []);

	useEffect(
		() => () => {
			if (review?.url) URL.revokeObjectURL(review.url);
		},
		[review?.url]
	);

	// Re-attach the stream when the live <video> remounts (review → record again).
	useEffect(() => {
		if ((phase === 'brief' || phase === 'recording') && liveRef.current && streamRef.current) {
			liveRef.current.srcObject = streamRef.current;
		}
	}, [phase]);

	const sampleLight = useCallback(() => {
		const video = liveRef.current;
		if (!video || video.readyState < 2) return;
		try {
			const canvas = document.createElement('canvas');
			canvas.width = 32;
			canvas.height = 18;
			const ctx = canvas.getContext('2d', { willReadFrequently: true });
			if (!ctx) return;
			ctx.drawImage(video, 0, 0, 32, 18);
			const { data } = ctx.getImageData(0, 0, 32, 18);
			let sum = 0;
			for (let i = 0; i < data.length; i += 4) sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
			setLowLight(sum / (data.length / 4) < LOW_LIGHT_LUMA);
		} catch {
			// A tainted or unsupported canvas — skip the hint.
		}
	}, []);

	const stop = useCallback(() => {
		clearTimers();
		const recorder = recorderRef.current;
		if (recorder && recorder.state !== 'inactive') recorder.stop();
	}, [clearTimers]);

	const start = useCallback(() => {
		const stream = streamRef.current;
		if (!stream) return;
		const mimeType = pickRecorderMime();
		let recorder: MediaRecorder;
		try {
			// ~1.2 Mbps keeps three minutes near 28 MB — legible scores, uploadable on 3G.
			const options: MediaRecorderOptions = { videoBitsPerSecond: 1_200_000, audioBitsPerSecond: 64_000 };
			recorder = new MediaRecorder(stream, mimeType ? { ...options, mimeType } : options);
		} catch {
			setErrorKey('unsupported');
			setPhase('error');
			return;
		}
		chunksRef.current = [];
		recorder.ondataavailable = (event) => {
			if (event.data.size) chunksRef.current.push(event.data);
		};
		recorder.onstop = () => {
			const endedAt = Date.now();
			const type = (recorder.mimeType || mimeType || 'video/webm').split(';')[0];
			const blob = new Blob(chunksRef.current, { type });
			const file = new File([blob], `result-video-${pollingUnitCode}-${endedAt}.${extensionFor(type)}`, { type });
			const durationSec = Math.round((endedAt - startedAtRef.current) / 100) / 10;
			setReview({
				url: URL.createObjectURL(blob),
				file,
				durationSec,
				startedAt: new Date(startedAtRef.current).toISOString(),
				endedAt: new Date(endedAt).toISOString(),
			});
			stopTracks();
			setPhase('review');
		};
		recorderRef.current = recorder;
		startedAtRef.current = Date.now();
		recorder.start(1000);
		setChapters([{ key: SHOTS[0].key, atSec: 0 }]);
		setShotIndex(0);
		setElapsed(0);
		setPhase('recording');
		vibrate(30);
		tickRef.current = window.setInterval(() => {
			const secs = (Date.now() - startedAtRef.current) / 1000;
			setElapsed(secs);
			if (secs >= maxDurationSec) stop();
		}, 250);
		lumaRef.current = window.setInterval(sampleLight, 2000);
	}, [maxDurationSec, pollingUnitCode, sampleLight, stop, stopTracks]);

	const nextShot = useCallback(() => {
		if (isLastShot) {
			stop();
			return;
		}
		const next = shotIndex + 1;
		const atSec = Math.round(((Date.now() - startedAtRef.current) / 1000) * 10) / 10;
		setChapters((c) => [...c, { key: SHOTS[next].key, atSec }]);
		setShotIndex(next);
		vibrate(20);
	}, [isLastShot, shotIndex, stop]);

	const recordAgain = useCallback(() => {
		setReview(null);
		setChecked(new Set());
		setLowLight(false);
		void openCamera();
	}, [openCamera]);

	const use = useCallback(() => {
		if (!review) return;
		onUse({
			file: review.file,
			durationSec: review.durationSec,
			captureStartedAt: review.startedAt,
			captureEndedAt: review.endedAt,
			chapters,
			coords,
			deviceInfo: deviceInfo(streamRef.current?.getVideoTracks()[0]?.label),
			wifiOnly,
		});
	}, [review, chapters, coords, wifiOnly, onUse]);

	const requestClose = useCallback(() => {
		if (phase === 'recording' && !confirmDiscard) {
			setConfirmDiscard(true);
			return;
		}
		onClose();
	}, [phase, confirmDiscard, onClose]);

	// A pending "Discard?" belongs to the take in progress; leaving that phase withdraws it.
	useEffect(() => {
		setConfirmDiscard(false);
	}, [phase]);

	// The second tap must come soon after the first, or the prompt quietly withdraws.
	useEffect(() => {
		if (!confirmDiscard) return;
		const timer = window.setTimeout(() => setConfirmDiscard(false), 4000);
		return () => window.clearTimeout(timer);
	}, [confirmDiscard]);

	const remaining = maxDurationSec - elapsed;
	const nearEnd = phase === 'recording' && remaining <= WARN_BEFORE_END_SEC;

	const content = (
		<div
			className='rv-rec'
			role='dialog'
			aria-modal='true'
			aria-label='Record a video of the result sheet'
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					e.preventDefault();
					requestClose();
				}
			}}
		>
			<div className='rv-rec__top'>
				{phase === 'recording' || phase === 'brief' ? (
					<ol
						className='rv-rec__rail'
						aria-label='Shots'
					>
						{SHOTS.map((s, i) => {
							const state = phase !== 'recording' ? 'todo' : i < shotIndex ? 'done' : i === shotIndex ? 'active' : 'todo';
							return (
								<li
									key={s.key}
									className={`rv-rec__rail-seg rv-rec__rail-seg--${state}`}
									aria-current={state === 'active' ? 'step' : undefined}
								>
									<span className='visually-hidden'>
										{s.title}
										{state === 'done' ? ' (done)' : ''}
									</span>
								</li>
							);
						})}
					</ol>
				) : null}
				<div className='rv-rec__topbar'>
					<span className='rv-rec__unit'>{pollingUnitName || pollingUnitCode}</span>
					{phase === 'recording' ? (
						<span
							className={`rv-rec__clock${nearEnd ? ' rv-rec__clock--warn' : ''}`}
							aria-live={nearEnd ? 'polite' : 'off'}
						>
							<span
								className='rv-rec__dot'
								aria-hidden='true'
							/>
							{formatClock(elapsed)} / {formatClock(maxDurationSec)}
							{nearEnd ? <span className='visually-hidden'>{Math.ceil(remaining)} seconds left</span> : null}
						</span>
					) : null}
					<button
						ref={closeRef}
						type='button'
						className={`rv-rec__close${confirmDiscard ? ' rv-rec__close--confirm' : ''}`}
						onClick={requestClose}
						aria-label={confirmDiscard ? 'Tap again to discard this recording' : 'Close recorder'}
					>
						{confirmDiscard ? (
							<span className='rv-rec__close-text'>Discard?</span>
						) : (
							<CloseSVG
								width='18'
								height='18'
								color='currentColor'
							/>
						)}
					</button>
				</div>
				{phase === 'recording' ? (
					<div
						className='rv-rec__cue'
						aria-live='assertive'
					>
						<span className='rv-rec__cue-step'>
							{shotIndex + 1} of {SHOTS.length}
						</span>
						<span className='rv-rec__cue-title'>{shot.title}</span>
						<span className='rv-rec__cue-hint'>
							{lowLight ? 'It looks dark — move closer to light or turn on a torch.' : shot.hint}
						</span>
					</div>
				) : null}
			</div>

			<div className={`rv-rec__stage${phase === 'review' ? ' rv-rec__stage--review' : ''}`}>
				{phase === 'error' ? (
					<div className='rv-rec__message'>
						<p>{ERRORS[errorKey]}</p>
						<button
							type='button'
							className='rv-rec__ghost'
							onClick={() => void openCamera()}
						>
							Try again
						</button>
					</div>
				) : phase === 'review' && review ? (
					// eslint-disable-next-line jsx-a11y/media-has-caption -- a just-recorded clip has no caption track
					<video
						ref={reviewRef}
						className='rv-rec__media'
						src={review.url}
						controls
						playsInline
					/>
				) : (
					<>
						<video
							ref={liveRef}
							className='rv-rec__media'
							autoPlay
							muted
							playsInline
						/>
						{phase === 'recording' && shot.frame ? (
							<div
								className='rv-rec__frame'
								aria-hidden='true'
							>
								<span />
								<span />
								<span />
								<span />
							</div>
						) : null}
						{phase === 'requesting' ? <div className='rv-rec__message'>Starting the camera…</div> : null}
					</>
				)}

				{phase === 'brief' ? (
					<div className='rv-rec__brief'>
						<p className='rv-rec__brief-lead'>Film these four shots in one take. You’ll be prompted as you go.</p>
						<ol className='rv-rec__brief-list'>
							{SHOTS.map((s) => (
								<li key={s.key}>
									<strong>{s.title}</strong>
									<span>{s.hint}</span>
								</li>
							))}
						</ol>
						<p className='rv-rec__brief-note'>
							Up to {formatClock(maxDurationSec)}. Hold the phone sideways for the sheet if it reads better.
						</p>
					</div>
				) : null}

				{phase === 'recording' && shot.key === 'scores' && partyScores.length ? (
					<div className='rv-rec__readalong'>
						<span className='rv-rec__readalong-title'>Your entered figures</span>
						<ul>
							{partyScores.map((p, i) => (
								<li key={`${p.name}-${i}`}>
									<button
										type='button'
										aria-pressed={checked.has(i)}
										onClick={() =>
											setChecked((prev) => {
												const next = new Set(prev);
												if (next.has(i)) next.delete(i);
												else next.add(i);
												return next;
											})
										}
									>
										<span className='rv-rec__readalong-name'>{p.name}</span>
										<span className='rv-rec__readalong-votes'>{p.votes.toLocaleString()}</span>
									</button>
								</li>
							))}
						</ul>
						<span className='rv-rec__readalong-hint'>Tap a row once it’s on camera.</span>
					</div>
				) : null}
			</div>

			<div className='rv-rec__controls'>
				{phase === 'brief' ? (
					<>
						<span className='rv-rec__gps'>
							{coords ? `Location ±${Math.round(coords.accuracy ?? 0)} m` : 'Finding your location…'}
						</span>
						<button
							type='button'
							className='rv-rec__shutter'
							onClick={start}
							aria-label='Start recording'
						>
							<span className='rv-rec__shutter-core' />
						</button>
						<span className='rv-rec__spacer' />
					</>
				) : null}

				{phase === 'recording' ? (
					<>
						<span className='rv-rec__gps'>{coords ? `±${Math.round(coords.accuracy ?? 0)} m` : 'No location'}</span>
						<button
							type='button'
							className='rv-rec__shutter rv-rec__shutter--recording'
							onClick={stop}
							aria-label='Stop recording'
						>
							<span className='rv-rec__shutter-core' />
						</button>
						<button
							type='button'
							className='rv-rec__next'
							onClick={nextShot}
						>
							{isLastShot ? 'Finish' : 'Next shot'}
						</button>
					</>
				) : null}

				{phase === 'review' && review ? (
					<div className='rv-rec__review'>
						{chapters.length > 1 ? (
							<div
								className='rv-rec__chapters'
								aria-label='Jump to a shot'
							>
								{chapters.map((c) => (
									<button
										key={c.key}
										type='button'
										onClick={() => {
											if (reviewRef.current) reviewRef.current.currentTime = c.atSec;
										}}
									>
										{SHOTS.find((s) => s.key === c.key)?.title ?? c.key}
										<span>{formatClock(c.atSec)}</span>
									</button>
								))}
							</div>
						) : null}
						<p className='rv-rec__review-meta'>
							{formatClock(review.durationSec)} · {formatMegabytes(review.file.size)}
							{chapters.length < SHOTS.length
								? ` · ${SHOTS.length - chapters.length} shot${SHOTS.length - chapters.length === 1 ? '' : 's'} skipped`
								: ''}
						</p>
						{meteredConnectionKnown() ? (
							<label className='rv-rec__wifi'>
								<input
									type='checkbox'
									checked={wifiOnly}
									onChange={(e) => setWifiOnly(e.target.checked)}
								/>
								Upload only on Wi-Fi
							</label>
						) : null}
						<div className='rv-rec__review-actions'>
							<button
								type='button'
								className='rv-rec__ghost'
								onClick={recordAgain}
							>
								Record again
							</button>
							<button
								type='button'
								className='rv-rec__primary'
								onClick={use}
							>
								Use this video
							</button>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);

	return createPortal(content, document.body);
};

export default ResultVideoRecorder;
